import React, { useState, useEffect } from 'react';
import { GNSSProcessingConfig, GNSSProcessingResult, GNSSJobStatus } from '../types.ts';
import { UploadIcon, DownloadIcon } from './icons.tsx';
import { Lock, AlertCircle, ExternalLink } from 'lucide-react';
import gnssService from '../services/gnssProcessingService.ts';
import gnssJobHistoryService from '../services/gnssJobHistoryService.ts';
import gnssAuthService from '../services/gnssAuthService.ts';
import GNSSJobHistoryPanel from './GNSSJobHistoryPanel.tsx';
import GNSSQuotaDisplay from './GNSSQuotaDisplay.tsx';
import { useAppState } from '../contexts/AppStateContext.tsx';

interface GNSSProcessingPanelProps {
  onClose: () => void;
  onShowApiKeyModal?: () => void;
  onGoHome?: () => void;
  hasApiKey?: boolean; // External API key state from parent
}

const GNSSProcessingPanel: React.FC<GNSSProcessingPanelProps> = ({ onClose, onShowApiKeyModal, onGoHome, hasApiKey: externalHasApiKey }) => {
  const [rinexFile, setRinexFile] = useState<File | null>(null);
  const [referenceFile, setReferenceFile] = useState<File | null>(null);
  const [processingConfig, setProcessingConfig] = useState<GNSSProcessingConfig>({
    mode: 'spp',
    constellations: ['gps'],
    outputInterval: 1,
    outputFormat: 'llh',
    ionosphereModel: 'broadcast',
    troposphereModel: 'saastammoinen',
    ambiguityResolution: 'off',
  });
  const [jobStatus, setJobStatus] = useState<GNSSJobStatus | null>(null);
  const [processingResult, setProcessingResult] = useState<GNSSProcessingResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [apiAvailable, setApiAvailable] = useState(false);
  const [localHasApiKey, setLocalHasApiKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [showJobHistory, setShowJobHistory] = useState(false);
  const [quota, setQuota] = useState<any>(null);
  const { addNotification } = useAppState();

  const notifyError = (message: string) => {
    addNotification({ kind: 'gnss-processing', severity: 'error', title: 'GNSS Processing', message });
  };

  // Use external API key state if provided, otherwise use local state
  const hasApiKey = externalHasApiKey !== undefined ? externalHasApiKey : localHasApiKey;

  // Check API availability and API key on mount and when storage changes
  useEffect(() => {
    const checkAPI = async () => {
      const isHealthy = await gnssService.checkAPIHealth();
      setApiAvailable(isHealthy);
      if (!isHealthy) {
        console.warn('GNSS API not available - using simulation mode');
      }
      
      // Check for saved API key
      const savedApiKey = localStorage.getItem('landsurv_user_api_key') || localStorage.getItem('gnss_api_key');
      setLocalHasApiKey(!!savedApiKey);
      
      // Load quota if API key exists
      if (savedApiKey) {
        const storedKey = gnssAuthService.getStoredApiKey();
        if (storedKey) {
          const userQuota = gnssAuthService.getUserQuota(storedKey.tier);
          setQuota(userQuota);
        }
      }
    };
    
    checkAPI();
    
    // Listen for storage changes from other tabs or parent component
    const handleStorageChange = () => {
      checkAPI();
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleSaveApiKey = () => {
    if (apiKeyInput.trim()) {
      // Validate API key format
      const validation = gnssAuthService.validateApiKey(apiKeyInput);
      if (!validation.valid) {
        notifyError(validation.error || 'Invalid API key');
        return;
      }

      localStorage.setItem('gnss_api_key', apiKeyInput);
      
      // Store API key in auth service
      gnssAuthService.storeApiKey(apiKeyInput, `user_${Date.now()}`, 'Web User', 'pro');
      
      // Create authenticated session
      const session = gnssAuthService.createSession(apiKeyInput);
      if (session) {
        // Load quota
        const storedKey = gnssAuthService.getStoredApiKey();
        if (storedKey) {
          const userQuota = gnssAuthService.getUserQuota(storedKey.tier);
          setQuota(userQuota);
        }
      }

      setLocalHasApiKey(true);
    }
  };

  const handleRinexFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setRinexFile(file);
    }
  };

  const handleReferenceFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setReferenceFile(file);
    }
  };

  const handleStartProcessing = async () => {
    if (!hasApiKey) {
      if (onShowApiKeyModal) {
        onShowApiKeyModal();
      } else {
        notifyError('API key required. Please add your API key to use the RINEX Agent.');
      }
      return;
    }

    if (!rinexFile) {
      notifyError('Please select a RINEX file');
      return;
    }

    // Check quota before processing
    if (quota) {
      const quotaCheck = gnssAuthService.canProcessJob(rinexFile.size, quota);
      if (!quotaCheck.allowed) {
        notifyError(`Quota exceeded: ${quotaCheck.reason}`);
        return;
      }
    }

    setIsProcessing(true);
    setUploadProgress(0);
    
    try {
      if (!apiAvailable) {
        // Fallback to simulation mode
        simulateProcessing();
        return;
      }

      // Submit job to backend with API key
      const apiKey = localStorage.getItem('gnss_api_key');
      const submitResponse = await gnssService.submitGNSSJob(
        {
          rinexFile,
          referenceFile: referenceFile || undefined,
          config: processingConfig,
        },
        (progress) => setUploadProgress(progress)
      );

      // Save job to history
      await gnssJobHistoryService.saveJob(
        submitResponse.jobId,
        rinexFile.name,
        processingConfig,
        rinexFile.size
      );

      // Set initial job status
      setJobStatus({
        jobId: submitResponse.jobId,
        status: 'processing',
        progress: 0,
        startTime: submitResponse.createdAt,
      });

      // Poll for job completion
      const finalStatus = await gnssService.pollJobStatus(
        submitResponse.jobId,
        (status) => {
          setJobStatus({
            jobId: status.jobId,
            status: status.status as any,
            progress: status.progress,
            startTime: status.createdAt,
            completedTime: status.completedAt,
            currentStep: status.currentStep,
            error: status.error,
          });
        }
      );

      if (finalStatus.status === 'completed') {
        // Fetch results
        const results = await gnssService.getJobResults(submitResponse.jobId, 'geojson');
        if (typeof results !== 'string') {
          setProcessingResult({
            jobId: submitResponse.jobId,
            completedAt: results.completedAt,
            statistics: results.statistics,
            results: results.results || [],
          });

          // Save completed job to history
          const duration = new Date(results.completedAt).getTime() - new Date(submitResponse.createdAt).getTime();
          await gnssJobHistoryService.markCompleted(
            submitResponse.jobId,
            {
              jobId: submitResponse.jobId,
              completedAt: results.completedAt,
              statistics: results.statistics,
              results: results.results || [],
            },
            duration
          );

          // Record usage in quota
          const storedKey = gnssAuthService.getStoredApiKey();
          if (storedKey) {
            gnssAuthService.recordJobUsage(storedKey.userId, rinexFile.size);
            // Refresh quota display
            const updatedQuota = gnssAuthService.getUserQuota(storedKey.tier);
            setQuota(updatedQuota);
          }
        }
      } else if (finalStatus.status === 'failed') {
        notifyError(`Job failed: ${finalStatus.error || 'Unknown error'}`);
        
        // Save failed job to history
        await gnssJobHistoryService.markFailed(
          submitResponse.jobId,
          finalStatus.error || 'Unknown error'
        );
      }

      setIsProcessing(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Processing error';
      notifyError(errorMessage);
      console.error('Processing error:', err);
      setIsProcessing(false);
    }
  };

  const simulateProcessing = () => {
    const simulatedJobId = `job_${Date.now()}`;
    setJobStatus({
      jobId: simulatedJobId,
      status: 'processing',
      progress: 0,
      startTime: new Date().toISOString(),
    });

    let progress = 0;
    const progressInterval = setInterval(() => {
      progress += Math.random() * 25;
      if (progress >= 100) {
        clearInterval(progressInterval);
        setJobStatus(prev => prev ? {
          ...prev,
          status: 'completed',
          progress: 100,
          completedTime: new Date().toISOString(),
        } : null);
        
        // Simulate mock results
        setProcessingResult({
          jobId: simulatedJobId,
          completedAt: new Date().toISOString(),
          statistics: {
            pointsProcessed: 1240,
            pdop: 2.5,
            horizontalRMS: 0.045,
            verticalRMS: 0.067,
          },
          results: [],
        });
        
        setIsProcessing(false);
      } else {
        setJobStatus(prev => prev ? { ...prev, progress: Math.min(progress, 99) } : null);
      }
    }, 800);
  };

  const handleConfigChange = (key: keyof GNSSProcessingConfig, value: any) => {
    setProcessingConfig(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleConstellationChange = (constellation: string, checked: boolean) => {
    setProcessingConfig(prev => ({
      ...prev,
      constellations: checked
        ? [...prev.constellations, constellation as any]
        : prev.constellations.filter(c => c !== constellation),
    }));
  };

  return (
    <div className="w-full bg-gray-900 text-gray-100">
      {/* Main Processing Panel */}
      <div className="w-full">
      {/* No API Key Warning */}
      {!hasApiKey && (
        <div className="bg-yellow-900/30 border border-yellow-600 p-4 mx-6 mt-6 rounded-lg flex gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-yellow-200 font-medium">API Key Required</p>
            <p className="text-sm text-yellow-300">
              <button
                onClick={() => onShowApiKeyModal?.()}
                className="underline hover:text-yellow-100 transition"
              >
                Unlock premium access
              </button>
              {' '}to use the RINEX Agent
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex-shrink-0 p-6 border-b border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-2xl font-bold text-indigo-400">RINEX Agent</h2>
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-500/20 border border-cyan-500/30 rounded-full text-xs font-semibold text-cyan-400">
                <Lock className="w-3 h-3" />
                Premium
              </span>
            </div>
            <p className="text-sm text-gray-400">
              Process RINEX files to generate high-precision GNSS solutions
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200 text-2xl"
          >
            ×
          </button>
        </div>
        
        {/* Header Controls */}
        <button
          onClick={() => setShowJobHistory(!showJobHistory)}
          className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-100 text-sm rounded-lg transition border border-gray-600"
        >
          {showJobHistory ? '← Hide' : 'View'} Job History
        </button>

        {/* Quota Display */}
        {quota && (
          <GNSSQuotaDisplay quota={quota} compact={true} />
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Disabled State If No API Key */}
        {!hasApiKey && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 text-center opacity-60 pointer-events-none">
            <p className="text-gray-400">Activate your API key to begin processing RINEX files</p>
          </div>
        )}

        {/* File Upload Section */}
        <div className="space-y-4" style={{ opacity: hasApiKey ? 1 : 0.5, pointerEvents: hasApiKey ? 'auto' : 'none' }}>
          <h3 className="text-lg font-semibold text-indigo-300">Input Files</h3>
          
          {/* RINEX File Upload */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              RINEX Observation File *
            </label>
            <div className="relative">
              <input
                type="file"
                accept=".rinex,.rnx,.obs"
                onChange={handleRinexFileSelect}
                disabled={isProcessing}
                className="hidden"
                id="rinex-file-input"
              />
              <label
                htmlFor="rinex-file-input"
                className={`flex items-center justify-center p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  rinexFile
                    ? 'border-indigo-500 bg-indigo-900/20'
                    : 'border-gray-600 hover:border-indigo-500 hover:bg-gray-700/50'
                } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="text-center">
                  <UploadIcon className="w-8 h-8 text-indigo-400 mx-auto mb-2" />
                  <p className="text-sm font-medium">
                    {rinexFile ? rinexFile.name : 'Drop RINEX file or click to select'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Accepted: .rinex, .rnx, .obs</p>
                </div>
              </label>
            </div>
          </div>

          {/* Reference Station File (Optional) */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Reference Station RINEX (Optional - for RTK mode)
            </label>
            <div className="relative">
              <input
                type="file"
                accept=".rinex,.rnx,.obs"
                onChange={handleReferenceFileSelect}
                disabled={isProcessing}
                className="hidden"
                id="reference-file-input"
              />
              <label
                htmlFor="reference-file-input"
                className={`flex items-center justify-center p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  referenceFile
                    ? 'border-indigo-500 bg-indigo-900/20'
                    : 'border-gray-600 hover:border-indigo-500 hover:bg-gray-700/50'
                } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="text-center">
                  <UploadIcon className="w-8 h-8 text-indigo-400 mx-auto mb-2" />
                  <p className="text-sm font-medium">
                    {referenceFile ? referenceFile.name : 'Drop reference RINEX or click to select'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Optional for RTK processing</p>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Processing Configuration */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-indigo-300">Processing Configuration</h3>
          
          {/* Processing Mode */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Processing Mode
            </label>
            <select
              value={processingConfig.mode}
              onChange={(e) => handleConfigChange('mode', e.target.value)}
              disabled={isProcessing}
              className="w-full px-3 py-2 bg-gray-700 text-gray-100 rounded border border-gray-600 hover:border-indigo-500 focus:border-indigo-500 focus:outline-none disabled:opacity-50"
            >
              <option value="spp">SPP (Single Point Positioning)</option>
              <option value="rtk">RTK (Real-Time Kinematic)</option>
              <option value="ppp">PPP (Precise Point Positioning)</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              {processingConfig.mode === 'spp' && 'Standalone positioning, ~5-10m accuracy'}
              {processingConfig.mode === 'rtk' && 'Relative positioning with base station, ~2-5cm accuracy'}
              {processingConfig.mode === 'ppp' && 'Precise positioning, ~10-30cm accuracy'}
            </p>
          </div>

          {/* Constellations */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <label className="block text-sm font-medium text-gray-300 mb-3">
              GNSS Constellations
            </label>
            <div className="grid grid-cols-2 gap-3">
              {['gps', 'glonass', 'galileo', 'beidou'].map((constellation) => (
                <label key={constellation} className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={processingConfig.constellations.includes(constellation as any)}
                    onChange={(e) => handleConstellationChange(constellation, e.target.checked)}
                    disabled={isProcessing}
                    className="w-4 h-4 rounded bg-gray-700 border-gray-600 checked:bg-indigo-600 cursor-pointer disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-300 capitalize">{constellation}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Output Format */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Output Format
            </label>
            <select
              value={processingConfig.outputFormat}
              onChange={(e) => handleConfigChange('outputFormat', e.target.value)}
              disabled={isProcessing}
              className="w-full px-3 py-2 bg-gray-700 text-gray-100 rounded border border-gray-600 hover:border-indigo-500 focus:border-indigo-500 focus:outline-none disabled:opacity-50"
            >
              <option value="llh">Latitude/Longitude/Height</option>
              <option value="xyz">Cartesian (X/Y/Z)</option>
              <option value="enu">East/North/Up</option>
            </select>
          </div>
        </div>

        {/* Job Status */}
        {jobStatus && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-indigo-300">Processing Status</h3>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-300">
                  {jobStatus.status === 'queued' && '⏳ Queued'}
                  {jobStatus.status === 'processing' && '⚙️ Processing'}
                  {jobStatus.status === 'completed' && '✓ Completed'}
                  {jobStatus.status === 'failed' && '✗ Failed'}
                </span>
                <span className="text-sm text-gray-400">{Math.round(jobStatus.progress)}%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div
                  className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${jobStatus.progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">Job ID: {jobStatus.jobId}</p>
            </div>
          </div>
        )}

        {/* Results Preview */}
        {processingResult && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-green-300">Processing Results</h3>
            <div className="bg-gray-800 rounded-lg p-4 border border-green-700 space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400">Total Positions</p>
                  <p className="text-lg font-bold text-gray-100">
                    {processingResult.statistics.totalEpochs}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Fixed Solutions</p>
                  <p className="text-lg font-bold text-green-400">
                    {processingResult.statistics.fixedPercentage.toFixed(1)}%
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Duration</p>
                  <p className="text-lg font-bold text-gray-100">
                    {processingResult.metadata.duration}s
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Mode</p>
                  <p className="text-lg font-bold text-gray-100">
                    {processingResult.metadata.processingMode.toUpperCase()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="flex-shrink-0 p-6 border-t border-gray-700 flex gap-3 justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-lg transition-colors"
          disabled={isProcessing}
        >
          Close
        </button>
        <button
          onClick={handleStartProcessing}
          disabled={!rinexFile || isProcessing}
          className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            !rinexFile || isProcessing
              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
              : 'bg-indigo-600 hover:bg-indigo-700 text-white'
          }`}
        >
          {isProcessing ? (
            <>
              <span className="animate-spin">⚙️</span>
              Processing...
            </>
          ) : (
            <>
              <DownloadIcon className="w-4 h-4" />
              Start Processing
            </>
          )}
        </button>
      </div>
    </div>
    </div>
  );
};

export default GNSSProcessingPanel;
